import { useState, useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, getAnimation, resolveImage, hexToRgba, resolveLinkProps, getCardRadius, getCardPadding, getCardGap, getIconSize, getGridCols } from '../utils';

gsap.registerPlugin(ScrollTrigger);
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import MediaRenderer, { resolveItemMedia } from './MediaRenderer';

export default function ServicesSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const mediaMode = config.media_mode || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const carouselInterval = (config.carousel_interval || 4) * 1000;
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const rawItems = content.items || [];
    const defaultItems = [
        { icon: '💼', title: 'Consultoria', description: 'Asesoramiento experto para impulsar tu negocio al siguiente nivel.' },
        { icon: '🎨', title: 'Diseno', description: 'Soluciones visuales creativas que conectan con tu audiencia.' },
        { icon: '💻', title: 'Desarrollo', description: 'Tecnologia de vanguardia para tus proyectos digitales.' },
        { icon: '📈', title: 'Marketing', description: 'Estrategias de crecimiento basadas en datos reales.' },
    ];
    const items = (rawItems.length > 0 ? rawItems : defaultItems).map(item => ({ ...item, ...resolveItemMedia(item, mediaMode, mediaFolders) }));
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'grid';
    const [hoveredIdx, setHoveredIdx] = useState(null);
    const [expandedIdx, setExpandedIdx] = useState(0);
    const gridRef = useRef(null);

    useEffect(() => {
        if (enableAnimations === false || !gridRef.current) return;
        const children = gridRef.current.children;
        if (!children || children.length === 0) return;
        const ctx = gsap.context(() => {
            gsap.from(children, {
                opacity: 0,
                y: 40,
                duration: 0.6,
                stagger: 0.08,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: gridRef.current,
                    start: 'top 85%',
                    once: true,
                },
            });
        }, gridRef);
        return () => ctx.revert();
    }, [enableAnimations, variant]);

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle };
    const columns = config.columns || 3;
    const cols = columns === 2 ? 'md:grid-cols-2' : columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';

    const itemAccent = (item) => item.color || s.accent;

    const LinkBtn = ({item}) => item.link ? (
        <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1 text-sm font-medium mt-4 hover:underline transition-colors" style={{ color: itemAccent(item) }}>
            {item.link_text || 'Conocer mas'}
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
        </a>
    ) : null;

    // ── Image cards (image on top) ──
    if (variant === 'image-cards') {
        const hoverClass = config.card_hover === 'none' ? '' : config.card_hover === 'lift' ? 'hover:-translate-y-2 hover:shadow-xl' : config.card_hover === 'grow' ? 'hover:scale-105' : config.card_hover === 'glow' ? `hover:shadow-lg` : 'hover:-translate-y-1';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${getGridCols(config) || cols} ${getCardGap(config) || 'gap-6'}`}>
                        {items.map((item, i) => {
                            const ac = itemAccent(item);
                            return (
                                <div key={i} className={`group ${card} overflow-hidden transition-all duration-500 ${hoverClass} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms', ...(config.card_hover === 'glow' ? { '--tw-shadow-color': hexToRgba(ac, 0.25) } : {}) }}>
                                    {(item.resolvedImage || item.carouselImages || item.video) ? (
                                        <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={ac} aspect="aspect-[16/10]" rounded="" className="group-hover:scale-105 transition-transform duration-500" />
                                    ) : item.icon ? (
                                        <div className="aspect-[16/10] flex items-center justify-center" style={{ backgroundColor: hexToRgba(ac, 0.06) }}>
                                            <span className="text-5xl group-hover:scale-110 transition-transform">{item.icon}</span>
                                        </div>
                                    ) : null}
                                    <div className="p-5">
                                        <h3 className="text-lg font-bold mb-2" style={{ color: s.text }}>{item.title}</h3>
                                        {item.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                        <LinkBtn item={item}/>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Image overlay (image background with dark overlay) ──
    if (variant === 'image-overlay') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {items.map((item, i) => {
                            const ac = itemAccent(item);
                            return (
                                <div key={i} className={`group relative rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-xl ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                    <div className="aspect-[4/5] relative">
                                        {(item.resolvedImage || item.carouselImages || item.video) ? (
                                            <div className="absolute inset-0"><MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={ac} aspect="aspect-[4/5]" rounded="" className="group-hover:scale-110 transition-transform duration-700" /></div>
                                        ) : (
                                            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${ac}, ${hexToRgba(ac, 0.8)})` }}/>
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"/>
                                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                                            {item.icon && <span className="text-3xl mb-3 block">{item.icon}</span>}
                                            <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                                            {item.description && <p className="text-sm text-white/80 leading-relaxed line-clamp-3">{item.description}</p>}
                                            {item.link && (
                                                <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1 text-sm font-medium mt-3 text-white hover:underline">
                                                    {item.link_text || 'Conocer mas'} <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Image split (image left + text right, alternating) ──
    if (variant === 'image-split') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="space-y-8">
                        {items.map((item, i) => {
                            const imgFirst = i % 2 === 0;
                            return (
                                <div key={i} className={`${card} overflow-hidden transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                    <div className={`grid grid-cols-1 md:grid-cols-2 ${imgFirst ? '' : 'md:direction-rtl'}`}>
                                        <div className={`aspect-[4/3] overflow-hidden ${imgFirst ? '' : 'md:order-2'}`}>
                                            {(item.resolvedImage || item.carouselImages || item.video) ? (
                                                <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[4/3]" rounded="" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.06) }}>
                                                    <span className="text-6xl">{item.icon || '📋'}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className={`p-6 md:p-8 flex flex-col justify-center ${imgFirst ? '' : 'md:order-1'}`}>
                                            {item.icon && <span className="text-2xl mb-3">{item.icon}</span>}
                                            <h3 className="text-xl font-bold mb-3" style={{ color: s.text }}>{item.title}</h3>
                                            {item.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                            <LinkBtn item={item}/>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Image hover reveal ──
    if (variant === 'image-hover') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {items.map((item, i) => {
                            const isHovered = hoveredIdx === i;
                            return (
                                <div key={i} className={`group relative rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}
                                    onMouseEnter={() => setHoveredIdx(i)} onMouseLeave={() => setHoveredIdx(null)}>
                                    <div className="aspect-[4/3] relative">
                                        {/* Default state: icon + text */}
                                        <div className={`absolute inset-0 flex flex-col items-center justify-center p-6 text-center transition-all duration-500 ${isHovered ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
                                            style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#ffffff' }}>
                                            {item.icon && <span className="text-4xl mb-3">{item.icon}</span>}
                                            <h3 className="text-lg font-bold" style={{ color: s.text }}>{item.title}</h3>
                                            {item.description && <p className="text-xs mt-2 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>}
                                        </div>
                                        {/* Hover state: image + overlay */}
                                        <div className={`absolute inset-0 transition-all duration-500 ${isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}`}>
                                            {(item.resolvedImage || item.carouselImages || item.video) ? (
                                                <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[4/3]" rounded="" />
                                            ) : (
                                                <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${s.accent}, ${hexToRgba(s.accent, 0.8)})` }}/>
                                            )}
                                            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center p-6 text-center text-white">
                                                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                                                {item.description && <p className="text-xs text-white/80 line-clamp-3">{item.description}</p>}
                                                {item.link && (
                                                    <a {...resolveLinkProps(item.link)} className="mt-3 text-xs font-medium text-white underline">{item.link_text || 'Conocer mas'}</a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Full image cards with overlay ──
    if (variant === 'full-image') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {items.map((item, i) => {
                            const ac = itemAccent(item);
                            return (
                                <div key={i} className={`group relative overflow-hidden rounded-2xl transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms', minHeight: '280px' }}>
                                    {(item.resolvedImage || item.carouselImages || item.video) ? (
                                        <div className="absolute inset-0"><MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={ac} aspect="" rounded="" className="absolute inset-0 w-full h-full group-hover:scale-110 transition-transform duration-700" /></div>
                                    ) : (
                                        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${ac}, ${hexToRgba(ac, 0.8)})` }} />
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                                    {/* Floating badge icon */}
                                    {item.icon && (
                                        <div className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-2xl z-10 group-hover:scale-110 transition-transform">
                                            {item.icon}
                                        </div>
                                    )}
                                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white z-10">
                                        <h3 className="text-lg font-bold mb-1">{item.title}</h3>
                                        {item.description && <p className="text-sm text-white/80 line-clamp-2">{item.description}</p>}
                                        <LinkBtn item={item}/>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Cards with accent border top ──
    if (variant === 'accent-top') {
        const hoverClass = config.card_hover === 'none' ? '' : config.card_hover === 'lift' ? 'hover:-translate-y-2 hover:shadow-xl' : config.card_hover === 'grow' ? 'hover:scale-105' : config.card_hover === 'glow' ? 'hover:shadow-lg' : 'hover:-translate-y-1';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${getGridCols(config) || cols} ${getCardGap(config) || 'gap-6'}`}>
                        {items.map((item, i) => {
                            const ac = itemAccent(item);
                            return (
                                <div key={i} className={`group ${card} overflow-hidden transition-all duration-500 ${hoverClass} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms', ...(config.card_hover === 'glow' ? { '--tw-shadow-color': hexToRgba(ac, 0.25) } : {}) }}>
                                    <div className="h-1.5 transition-all duration-300 group-hover:h-2" style={{ backgroundColor: ac }} />
                                    {(item.resolvedImage || item.carouselImages || item.video) && <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={ac} aspect="aspect-[16/10]" rounded="" />}
                                    <div className={getCardPadding(config) || 'p-6'}>
                                        {item.icon && <span className="text-3xl mb-3 block">{item.icon}</span>}
                                        <h3 className="text-lg font-bold mb-2" style={{ color: s.text }}>{item.title}</h3>
                                        {item.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                        <LinkBtn item={item}/>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Hover border (inteligente360 style - horizontal cards with icon left, hover green border + shadow) ──
    if (variant === 'hover-border') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`max-w-[1400px] mx-auto px-[5%] ${anim}`}>
                    {(content.tag || content.title) && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className={`grid ${getGridCols(config) || (`grid-cols-1 sm:grid-cols-2 ${columns === 2 ? '' : columns === 4 ? 'lg:grid-cols-4' : columns === 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-3'}`)} ${getCardGap(config) || 'gap-4'}`}>
                        {items.map((item, i) => {
                            const ac = itemAccent(item);
                            const isHovered = hoveredIdx === i;
                            return (
                                <div key={i} className={`flex items-center gap-3 cursor-pointer transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{
                                        transitionDelay: visible ? `${i * 80}ms` : '0ms',
                                        padding: '10px 8px',
                                        borderRadius: '8px',
                                        backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#ffffff',
                                        border: `1px solid ${isHovered ? ac : (s.dark ? 'rgba(255,255,255,0.1)' : '#d7d7d7')}`,
                                        boxShadow: isHovered ? '0 0 30px 0 rgba(14,41,50,0.2)' : 'none',
                                    }}
                                    onMouseEnter={() => setHoveredIdx(i)} onMouseLeave={() => setHoveredIdx(null)}>
                                    {/* Icon - left side ~27% */}
                                    {item.icon && (
                                        <div className="shrink-0 w-[45px] h-[45px] flex items-center justify-center">
                                            <span className="text-3xl">{item.icon}</span>
                                        </div>
                                    )}
                                    {/* Info - right side ~73% */}
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-bold leading-snug transition-colors duration-300"
                                            style={{ fontSize: 'clamp(13px, 1.4vw, 18px)', color: isHovered ? ac : s.text }}>
                                            {item.title}
                                        </h4>
                                        {item.description && <p className="leading-snug mt-0.5 line-clamp-2"
                                            style={{ fontSize: 'clamp(11px, 0.9vw, 14px)', color: s.textSub }}>
                                            {item.description}
                                        </p>}
                                    </div>
                                    {/* Arrow icon right */}
                                    <div className="shrink-0 w-[25px] h-[25px] rounded-full flex items-center justify-center transition-all duration-300"
                                        style={{ backgroundColor: isHovered ? hexToRgba(ac, 0.08) : (s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6') }}>
                                        <svg className="w-3 h-3" style={{ color: isHovered ? ac : s.textSub }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Stacked scroll cards ──
    if (variant === 'stacked') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="space-y-4 max-w-3xl mx-auto">
                        {items.map((item, i) => {
                            const ac = itemAccent(item);
                            const scale = 1 - (items.length - 1 - i) * 0.03;
                            const bgLerp = Math.round(248 - (items.length - 1 - i) * 8);
                            return (
                                <div key={i} className={`group sticky top-24 rounded-2xl overflow-hidden shadow-lg transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{
                                        transitionDelay: visible ? `${i * 100}ms` : '0ms',
                                        transform: `scale(${scale})`,
                                        backgroundColor: s.dark ? `rgba(255,255,255,${0.03 + i * 0.02})` : `rgb(${bgLerp},${bgLerp},${bgLerp})`,
                                        zIndex: i + 1,
                                    }}>
                                    <div className="p-6 md:p-8 flex items-start gap-5">
                                        {/* Left: icon or image */}
                                        {(item.resolvedImage || item.icon) && (
                                            <div className="shrink-0">
                                                {item.resolvedImage ? (
                                                    <div className="w-16 h-16 rounded-xl overflow-hidden">
                                                        <img src={item.resolvedImage} alt="" className="w-full h-full object-cover"/>
                                                    </div>
                                                ) : (
                                                    <div className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl" style={{backgroundColor: hexToRgba(ac, 0.1)}}>
                                                        {item.icon}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {/* Right: text */}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-bold mb-2" style={{color: s.text}}>{item.title}</h3>
                                            {item.description && <p className="text-sm leading-relaxed" style={{color: s.textSub}}>{item.description}</p>}
                                            <LinkBtn item={item}/>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Expandable cards (CometaEdu-style: click to expand one at a time) ──
    if (variant === 'expandable') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="flex flex-col md:flex-row gap-3">
                        {items.map((item, i) => {
                            const ac = itemAccent(item);
                            const isOpen = expandedIdx === i;
                            return (
                                <div key={i} onClick={() => setExpandedIdx(i)}
                                    className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${isOpen ? 'md:flex-[3]' : 'md:flex-1'}`}
                                    style={{
                                        transitionDelay: visible ? `${i * 80}ms` : '0ms',
                                        backgroundColor: isOpen ? (s.dark ? 'rgba(255,255,255,0.08)' : '#ffffff') : (s.dark ? 'rgba(255,255,255,0.03)' : '#f9fafb'),
                                        border: `1px solid ${isOpen ? hexToRgba(ac, 0.3) : (s.dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb')}`,
                                        minHeight: '200px',
                                    }}>
                                    <div className="p-6 h-full flex flex-col">
                                        <div className="flex items-center gap-3 mb-3">
                                            {item.icon && (
                                                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl shrink-0 transition-all duration-300"
                                                    style={{backgroundColor: hexToRgba(ac, isOpen ? 0.12 : 0.06)}}>
                                                    {item.icon}
                                                </div>
                                            )}
                                            <h3 className="text-base font-bold transition-colors" style={{color: isOpen ? ac : s.text}}>{item.title}</h3>
                                        </div>
                                        <div className={`transition-all duration-500 overflow-hidden ${isOpen ? 'opacity-100 max-h-96' : 'opacity-0 max-h-0'}`}>
                                            {item.description && <p className="text-sm leading-relaxed mb-4" style={{color: s.textSub}}>{item.description}</p>}
                                            {(item.resolvedImage || item.carouselImages) && (
                                                <div className="rounded-xl overflow-hidden mt-2">
                                                    <MediaRenderer item={item} fitClass="object-cover" interval={4000} accent={ac} aspect="aspect-[16/9]" rounded="rounded-xl"/>
                                                </div>
                                            )}
                                            <LinkBtn item={item}/>
                                        </div>
                                        {!isOpen && item.description && <p className="text-xs mt-1 line-clamp-2" style={{color: s.textSub}}>{item.description}</p>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Grayscale reveal (cards start grayscale, become colorful on scroll/hover) ──
    if (variant === 'grayscale-reveal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {items.map((item, i) => {
                            const ac = itemAccent(item);
                            const isHovered = hoveredIdx === i;
                            return (
                                <div key={i} className={`group ${card} overflow-hidden transition-all duration-700 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{
                                        transitionDelay: visible ? `${i * 100}ms` : '0ms',
                                        filter: isHovered ? 'grayscale(0)' : 'grayscale(1)',
                                        transition: 'filter 500ms cubic-bezier(.2,.8,.2,1), transform 500ms, opacity 500ms, background 300ms ease',
                                    }}
                                    onMouseEnter={() => setHoveredIdx(i)} onMouseLeave={() => setHoveredIdx(null)}>
                                    {(item.resolvedImage || item.carouselImages || item.video) && (
                                        <MediaRenderer item={item} fitClass="object-cover" interval={4000} accent={ac} aspect="aspect-[16/10]" rounded="" className="group-hover:scale-105 transition-transform duration-500"/>
                                    )}
                                    <div className="p-5">
                                        {item.icon && (
                                            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 text-2xl transition-all"
                                                style={{backgroundColor: hexToRgba(ac, isHovered ? 0.12 : 0.06)}}>
                                                {item.icon}
                                            </div>
                                        )}
                                        <h3 className="text-lg font-bold mb-2 transition-colors" style={{color: isHovered ? ac : s.text}}>{item.title}</h3>
                                        {item.description && <p className="text-sm leading-relaxed" style={{color: s.textSub}}>{item.description}</p>}
                                        <LinkBtn item={item}/>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Timeline (horizontal on desktop, vertical on mobile) ──
    if (variant === 'timeline') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    {/* Desktop: horizontal timeline */}
                    <div className="hidden md:block overflow-x-auto pb-4">
                        <div className="relative flex items-start" style={{ minWidth: `${items.length * 220}px` }}>
                            {/* Connecting line */}
                            <div className="absolute top-7 left-7 right-7 h-0.5" style={{ backgroundColor: hexToRgba(s.accent, 0.2) }}/>
                            {items.map((item, i) => {
                                const ac = itemAccent(item);
                                return (
                                    <div key={i} className={`flex-1 flex flex-col items-center text-center px-4 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                        style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms', minWidth: '180px' }}>
                                        <div className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg mb-4"
                                            style={{ backgroundColor: ac }}>
                                            {item.icon || (i + 1)}
                                        </div>
                                        <h3 className="text-base font-bold mb-2" style={{ color: s.text }}>{item.title}</h3>
                                        {item.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                        <LinkBtn item={item}/>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {/* Mobile: vertical timeline */}
                    <div className="md:hidden relative pl-10">
                        {/* Vertical line */}
                        <div className="absolute left-5 top-0 bottom-0 w-0.5" style={{ backgroundColor: hexToRgba(s.accent, 0.2) }}/>
                        {items.map((item, i) => {
                            const ac = itemAccent(item);
                            return (
                                <div key={i} className={`relative mb-8 last:mb-0 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                                    <div className="absolute -left-10 top-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg z-10"
                                        style={{ backgroundColor: ac }}>
                                        {item.icon || (i + 1)}
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-base font-bold mb-1" style={{ color: s.text }}>{item.title}</h3>
                                        {item.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                        <LinkBtn item={item}/>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Comparison (Antes/Despues) ──
    if (variant === 'comparison') {
        const [mobileView, setMobileView] = useState('after');
        const half = Math.ceil(items.length / 2);
        const leftItems = items.slice(0, half);
        const rightItems = items.slice(half);
        const labelBefore = content.label_before || 'Antes / Tradicional';
        const labelAfter = content.label_after || 'Despues / Moderno';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    {/* Mobile toggle */}
                    <div className="flex md:hidden justify-center mb-6">
                        <div className="inline-flex rounded-full p-1" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                            <button onClick={() => setMobileView('before')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${mobileView === 'before' ? 'text-white shadow-sm' : ''}`}
                                style={mobileView === 'before' ? { backgroundColor: '#9ca3af', color: '#fff' } : { color: s.textSub }}>
                                {labelBefore}
                            </button>
                            <button onClick={() => setMobileView('after')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${mobileView === 'after' ? 'text-white shadow-sm' : ''}`}
                                style={mobileView === 'after' ? { backgroundColor: s.accent, color: '#fff' } : { color: s.textSub }}>
                                {labelAfter}
                            </button>
                        </div>
                    </div>
                    {/* Desktop: side-by-side */}
                    <div className="hidden md:grid grid-cols-2 gap-8">
                        {/* Left column — "Antes" */}
                        <div>
                            <div className="text-center mb-6">
                                <span className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6', color: s.textSub }}>
                                    {labelBefore}
                                </span>
                            </div>
                            <div className="space-y-4">
                                {leftItems.map((item, i) => (
                                    <div key={i} className={`rounded-xl p-5 transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
                                        style={{ transitionDelay: visible ? `${i * 120}ms` : '0ms', backgroundColor: s.dark ? 'rgba(255,255,255,0.04)' : '#f9fafb', border: `1px solid ${s.dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}` }}>
                                        <div className="flex items-start gap-3">
                                            {item.icon && <span className="text-2xl shrink-0 opacity-50">{item.icon}</span>}
                                            <div>
                                                <h4 className="font-semibold text-sm mb-1" style={{ color: s.textSub }}>{item.title}</h4>
                                                {item.description && <p className="text-xs leading-relaxed" style={{ color: hexToRgba(s.textSub, 0.7) }}>{item.description}</p>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Right column — "Despues" */}
                        <div>
                            <div className="text-center mb-6">
                                <span className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold text-white" style={{ backgroundColor: s.accent }}>
                                    {labelAfter}
                                </span>
                            </div>
                            <div className="space-y-4">
                                {rightItems.map((item, i) => (
                                    <div key={i} className={`rounded-xl p-5 transition-all duration-500 hover:-translate-y-0.5 hover:shadow-lg ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
                                        style={{ transitionDelay: visible ? `${i * 120}ms` : '0ms', backgroundColor: s.dark ? 'rgba(255,255,255,0.06)' : '#ffffff', border: `1px solid ${hexToRgba(s.accent, 0.2)}`, boxShadow: `0 2px 8px ${hexToRgba(s.accent, 0.08)}` }}>
                                        <div className="flex items-start gap-3">
                                            {item.icon && <span className="text-2xl shrink-0">{item.icon}</span>}
                                            <div>
                                                <h4 className="font-semibold text-sm mb-1" style={{ color: s.text }}>{item.title}</h4>
                                                {item.description && <p className="text-xs leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    {/* Mobile: toggle view */}
                    <div className="md:hidden space-y-4">
                        {(mobileView === 'before' ? leftItems : rightItems).map((item, i) => {
                            const isBefore = mobileView === 'before';
                            return (
                                <div key={i} className={`rounded-xl p-5 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{
                                        transitionDelay: visible ? `${i * 100}ms` : '0ms',
                                        backgroundColor: isBefore ? (s.dark ? 'rgba(255,255,255,0.04)' : '#f9fafb') : (s.dark ? 'rgba(255,255,255,0.06)' : '#ffffff'),
                                        border: `1px solid ${isBefore ? (s.dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb') : hexToRgba(s.accent, 0.2)}`,
                                    }}>
                                    <div className="flex items-start gap-3">
                                        {item.icon && <span className={`text-2xl shrink-0 ${isBefore ? 'opacity-50' : ''}`}>{item.icon}</span>}
                                        <div>
                                            <h4 className="font-semibold text-sm mb-1" style={{ color: isBefore ? s.textSub : s.text }}>{item.title}</h4>
                                            {item.description && <p className="text-xs leading-relaxed" style={{ color: isBefore ? hexToRgba(s.textSub, 0.7) : s.textSub }}>{item.description}</p>}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Grid (default — emoji only) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div ref={gridRef} className={`grid grid-cols-1 ${getGridCols(config) || cols} ${getCardGap(config) || 'gap-6'}`}>
                    {items.map((item, i) => {
                        const ac = itemAccent(item);
                        return (
                            <div key={i} className={`group ${card} ${getCardPadding(config) || 'p-6'} text-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms', borderColor: hoveredIdx === i && item.color ? hexToRgba(ac, 0.31) : undefined }}
                                onMouseEnter={(e) => {
                                    setHoveredIdx(i);
                                    gsap.to(e.currentTarget, {
                                        y: -8,
                                        scale: 1.02,
                                        boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                                        duration: 0.35,
                                        ease: 'power2.out',
                                    });
                                }}
                                onMouseLeave={(e) => {
                                    setHoveredIdx(null);
                                    gsap.to(e.currentTarget, {
                                        y: 0,
                                        scale: 1,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        duration: 0.35,
                                        ease: 'power2.out',
                                    });
                                }}>
                                {item.icon && (
                                    <div className={`mx-auto ${getIconSize(config) || 'w-14 h-14'} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                                        style={{ backgroundColor: hexToRgba(ac, 0.08) }}>
                                        <span className="text-3xl">{item.icon}</span>
                                    </div>
                                )}
                                <h3 className="text-lg font-bold mb-2" style={{ color: s.text }}>{item.title}</h3>
                                {item.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                <LinkBtn item={item}/>
                            </div>
                        );
                    })}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
